import React, { CSSProperties, ChangeEvent, useEffect, useRef, useState } from 'react';

import { useImmer } from 'use-immer';
import './FeedBlueprintEditor.scss';
import { RouteComponentProps } from 'react-router';
import { ObjectId } from 'bson';

import { useActiveProperty } from '@go-client/context/ActivePropertyContext';
import { BrandLogo } from '@go-client/components/UIkit/Brand/BrandLogo/BrandLogo';
import { PropertyBanner } from '@go-client/components/UIkit/Property/PropertyBanner';
import { MenuBar } from '@go-client/components';
import {
  BubbleButton,
  BubbleInput,
  BubbleSelect,
  BubbleTextArea,
  BubbleTypeahead,
  BubbleFileM,
} from '@go-client/components/BubbleUI';
import { getFeedTemplatesByProperty, getFonts, uploadFont } from '@go-client/lib/api/feedBlueprint';
import {
  BareFeedArea,
  BareFeedPage,
  BareFeedTemplate,
  FeedArea,
  FeedContentType,
  FeedContentWidgetType,
  FeedEditorFont,
} from '@go-shared/types/feedTemplate';
import { VenueRequestFile } from '@go-shared/types/groupBooking';
import { saveFeedTemplate, uploadPageBackgroundImage } from '@go-client/lib/api/feedBlueprint';
import { lastEl } from '@go-shared/lib/functional';
import { Property } from '@go-shared/types/property';
import Config from '@go-client/lib/config';
import { toast } from '@go-client/components/UIkit/Toast/Toast';
import { EventRundownTimeGrouping, FeedEditorElementType } from '@go-shared/types/contentTemplate';
import { Popup } from '@spark-labs/avocado-ui';
import { AreasTree } from '@go-client/components/FeedBlueprintEditor/AreasTree';
import { ContentTypeParams } from '@go-client/components/FeedBlueprintEditor/ContentTypeParams';
import { useUserInfo } from '@go-client/context/UserInfoContext';
import { canViewJsonEditorButton } from '@go-shared/lib/permissions';
import { UPLOAD_RULES } from '@go-shared/lib/uploads';
import FeedBlueprintJson from './FeedBlueprintJson';
import {
  BubbleTypeaheadOption,
  BubbleTypeaheadValue,
} from '@go-client/components/BubbleUI/BubbleTypeahead';
import { BrandAttribute, BrandLevel, BrandSegment, BrandVenueType } from '@go-shared/types/brand';

type SelectOption = {
  value: string;
  label: string;
};

type ContentType = FeedContentType | FeedEditorElementType | FeedContentWidgetType;

type ContentTypeOptions = {
  value: ContentType;
  label: ContentType;
};

const FeedBlueprintEditor: React.FC<RouteComponentProps> = props => {
  const variant = 'navy';
  const [selectedFeedTemplate, setSelectedFeedTemplate] = useState<SelectOption>();
  const [selectedPage, setSelectedPage] = useState<SelectOption>();
  const { activeProperty } = useActiveProperty();
  const [feedTemplates, setFeedTemplates] = useState<BareFeedTemplate[]>([]);
  const [feedTemplate, setFeedTemplate] = useImmer<BareFeedTemplate>(
    getNewFeedTemplate(activeProperty)
  );
  const areaIndicesRef = useRef<number[] | null>(null);
  const currentPageIndex = Number(selectedPage?.value) || 0;
  const currentPage = feedTemplate.pages[currentPageIndex];
  const currentArea = findNestedArea(currentPage.areas);
  const [hoveredArea, setHoveredArea] = useState<BareFeedArea | FeedArea | null>(null);
  const [selectedContentType, setSelectedContentType] = useState<ContentTypeOptions>({
    value: currentArea?.contentType!,
    label: currentArea?.contentType!,
  });
  const [fonts, setFonts] = useState<string[]>([]);
  const [selectedFonts, setSelectedFonts] = useState<BubbleTypeaheadValue<FeedEditorFont>[]>([]);
  const [showContentTypePopup, setShowContentTypePopup] = useState(false);
  const [showJsonEditor, setShowJsonEditor] = useState(false);
  const [shouldCascadeUpdate, setShouldCascadeUpdate] = useState(false);

  const { user } = useUserInfo();

  const bgImagePath = currentPage.image
    ? `${Config.PUBLIC_ASSETS_URL}/feeds/${currentPage.image}`
    : '';
  const feedTemplateOptions: SelectOption[] = [
    {
      value: '',
      label: 'New feed template',
    },
    ...feedTemplates.map(feedTemplate => ({
      value: feedTemplate._id,
      label: feedTemplate.name,
    })),
  ];
  const pageOptions: SelectOption[] = feedTemplate.pages.map((_, index) => ({
    value: index.toString(),
    label: `Page ${index + 1}`,
  }));
  const fontOptions: BubbleTypeaheadOption<FeedEditorFont>[] = fonts.map(font => ({
    key: font,
    item: {
      file: font,
      name: font.split('.')[0],
    },
  }));
  const files: VenueRequestFile[] = currentPage.image
    ? [
        {
          _id: '123',
          name: currentPage.image,
          location: bgImagePath,
        },
      ]
    : [];

  useEffect(() => {
    upsertCustomStyleElement(feedTemplate.customAreasCss);
  }, [feedTemplate.customAreasCss]);

  useEffect(() => {
    loadFeedTemplates();
    loadFonts();
  }, []);

  async function loadFeedTemplates() {
    const feedTemplates = await getFeedTemplatesByProperty(activeProperty._id);

    setFeedTemplates(feedTemplates);
  }

  async function loadFonts() {
    const fonts = await getFonts();

    setFonts(fonts);
  }

  function setActiveFeedTemplate(selected: SelectOption) {
    const feed = feedTemplates.find(ft => ft._id === selected.value);

    setSelectedPage({
      value: Number(0).toString(),
      label: 'Page 1',
    });
    setSelectedFeedTemplate(selected);
    areaIndicesRef.current = null;

    if (feed) {
      const selectedFonts = (feed.fonts || []).map(font => ({
        key: font.file,
        refKey: font.file,
        item: font,
      }));
      setFeedTemplate(feed);
      setSelectedFonts(selectedFonts);
    } else {
      setFeedTemplate(getNewFeedTemplate(activeProperty));
      setSelectedFonts([]);
    }
  }

  function setFeedTemplateName(nextName: string) {
    setFeedTemplate(draft => {
      draft.name = nextName;
    });
  }

  function setFeedTemplateWidth(nextWidth: number) {
    setFeedTemplate(draft => {
      draft.dimensions.width = nextWidth;
      draft.renderedDimensions!.width = nextWidth;
    });
  }

  function setFeedTemplateHeight(nextHeight: number) {
    setFeedTemplate(draft => {
      draft.dimensions.height = nextHeight;
      draft.renderedDimensions!.height = nextHeight;
    });
  }

  function deletePage() {
    const newPageIndex = currentPageIndex === 0 ? 0 : currentPageIndex - 1;

    setFeedTemplate(draft => {
      draft.pages.splice(currentPageIndex, 1);
    });
    setSelectedPage({
      value: newPageIndex.toString(),
      label: `Page ${newPageIndex + 1}`,
    });
    areaIndicesRef.current = null;
  }

  function addPage() {
    const newPageIndex = feedTemplate.pages.length;

    setFeedTemplate(draft => {
      draft.pages.push(getNewPage());
    });
    setSelectedPage({
      value: newPageIndex.toString(),
      label: `Page ${newPageIndex + 1}`,
    });
    areaIndicesRef.current = null;
  }

  function onSelectPage(selected: SelectOption) {
    setSelectedPage(selected);
    areaIndicesRef.current = null;
  }

  function handleOnAddFont(value: BubbleTypeaheadValue<FeedEditorFont>) {
    const newSelectedFonts = [...selectedFonts, value];

    setSelectedFonts(newSelectedFonts);
    setFeedTemplate(draft => {
      draft.fonts = newSelectedFonts.map(selectedFont => ({
        file: selectedFont.item.file,
        name: selectedFont.item.name,
      }));
    });
  }

  function handleOnRemoveFont(value: BubbleTypeaheadValue<FeedEditorFont>) {
    const newSelectedFonts = selectedFonts.filter(sf => sf.key !== value.key);

    setSelectedFonts(newSelectedFonts);
    setFeedTemplate(draft => {
      draft.fonts = newSelectedFonts.map(selectedFont => ({
        file: selectedFont.item.file,
        name: selectedFont.item.name,
      }));
    });
  }

  async function onUploadBackgroundImage(file: File) {
    const relativeLoc = await uploadPageBackgroundImage(file);
    const image = lastEl(relativeLoc.split('/'));

    setFeedTemplate(draft => {
      draft.pages[currentPageIndex].image = image;
    });
  }

  async function onUploadFont(file: File) {
    await uploadFont(file);
    toast.success('Saved font successfully');
    await loadFonts();
  }

  async function onSave() {
    const feed = await saveFeedTemplate(feedTemplate, shouldCascadeUpdate);
    await loadFeedTemplates();

    setSelectedFeedTemplate({
      value: feed._id,
      label: feed.name,
    });
    setFeedTemplate(feed);

    toast.success('Saved feed template successfully');
  }

  function onAreaClick(event: React.MouseEvent<HTMLDivElement, MouseEvent>, indices: number[]) {
    event.stopPropagation();
    areaIndicesRef.current = indices;
    const area = findNestedArea(currentPage.areas);
    setSelectedContentType({
      value: area?.contentType!,
      label: area?.contentType!,
    });
  }

  const createAreas = (areas: BareFeedArea[]) => {
    return areas.map((area, index) => {
      let childAreas;

      if (area.children?.length) {
        childAreas = createChildAreas(area.children, [index]);
      }

      const isActive = area === currentArea;
      const isHovered = area === hoveredArea;
      const activeClass = isActive ? 'active' : '';
      const hoverStyles: CSSProperties =
        isHovered && !isActive
          ? {
              borderWidth: '3px',
              borderStyle: 'dashed',
              borderColor: 'blue',
            }
          : {};

      return (
        <div
          key={area._id}
          className={`ContentBlock absolute ${activeClass} ${area.name}`}
          style={{
            ...hoverStyles,
            left: area.rectangle.x,
            top: area.rectangle.y,
            width: area.rectangle.width,
            height: area.rectangle.height,
          }}
          onClick={e => onAreaClick(e, [index])}
          onMouseOver={() => setHoveredArea(area)}
          onMouseLeave={() => setHoveredArea(null)}
        >
          {childAreas}
        </div>
      );
    });
  };

  const createChildAreas = (areas: FeedArea[], indices: number[]) => {
    return areas.map((area, index) => {
      let childAreas;

      if (area.children?.length) {
        childAreas = createChildAreas(area.children, [...indices, index]);
      }

      const isActive = area === currentArea;
      const isHovered = area === hoveredArea;
      const activeClass = isActive ? 'active' : '';
      const hoverStyles: CSSProperties =
        isHovered && !isActive
          ? {
              borderWidth: '3px',
              borderStyle: 'dashed',
              borderColor: 'blue',
            }
          : {};

      return (
        <div
          key={area._id}
          className={`ContentBlock_child ${activeClass} ${area.name}`}
          style={{ ...hoverStyles, width: '100%', height: '100%' }}
          onClick={e => onAreaClick(e, [...indices, index])}
          onMouseOver={e => {
            e.stopPropagation();
            setHoveredArea(area);
          }}
          onMouseLeave={() => setHoveredArea(null)}
        >
          {childAreas}
        </div>
      );
    });
  };

  function findNestedArea(
    areas: BareFeedArea[] | FeedArea[],
    indices: number[] | null = areaIndicesRef.current
  ): BareFeedArea | FeedArea | null {
    if (!indices) {
      return null;
    }

    const [index, ...rest] = indices;

    if (!rest.length) {
      return areas[index!];
    }

    return findNestedArea(areas[index!].children!, rest);
  }

  function findNestedAreas(
    areas: BareFeedArea[] | FeedArea[],
    indices: number[] | null = areaIndicesRef.current
  ): BareFeedArea[] | FeedArea[] | null {
    if (!indices) {
      return null;
    }

    const [index, ...rest] = indices;

    if (!rest.length) {
      return areas;
    }

    return findNestedAreas(areas[index!].children!, rest);
  }

  function isChildArea(): boolean {
    if (!areaIndicesRef.current) {
      return true;
    }

    return areaIndicesRef.current?.length > 1 ? true : false;
  }

  function setAreaName(nextName: string) {
    setFeedTemplate(draft => {
      const area = findNestedArea(draft.pages[currentPageIndex].areas);
      area!.name = nextName;
    });
  }

  function setAreaClassName(nextClassName: string) {
    setFeedTemplate(draft => {
      const area = findNestedArea(draft.pages[currentPageIndex].areas);
      area!.className = nextClassName;
    });
  }

  function setCssBundle(nextCssBundle: string) {
    setFeedTemplate(draft => {
      draft.cssBundle = nextCssBundle;
    });
  }

  function setActiveContentType(selected: ContentTypeOptions) {
    setSelectedContentType(selected);
    setFeedTemplate(draft => {
      const area = findNestedArea(draft.pages[currentPageIndex].areas);
      area!.contentType = selected.value;
      area!.params = {};
    });
  }

  function getContentTypeButton(): React.JSX.Element {
    switch (selectedContentType.value) {
      case 'filtered-rundown':
      case 'multi-day':
        return (
          <div className="text-center">
            <BubbleButton variant={variant} onClick={() => setShowContentTypePopup(true)}>
              Show Content Type Params
            </BubbleButton>
          </div>
        );
      default:
        return <></>;
    }
  }

  function onSelectSegments(selected: BubbleTypeaheadValue<BrandSegment>[]) {
    setFeedTemplate(draft => {
      const area = findNestedArea(draft.pages[currentPageIndex].areas);

      ensureParamsExists(area!);
      area!.params!.eventRundownFilter!.segments = selected.map(selected => selected.item._id);
    });
  }

  function onSelectLevels(selected: BubbleTypeaheadValue<BrandLevel>[]) {
    setFeedTemplate(draft => {
      const area = findNestedArea(draft.pages[currentPageIndex].areas);

      ensureParamsExists(area!);
      area!.params!.eventRundownFilter!.levels = selected.map(selected => selected.item._id);
    });
  }

  function onSelectAttributes(selected: BubbleTypeaheadValue<BrandAttribute>[]) {
    setFeedTemplate(draft => {
      const area = findNestedArea(draft.pages[currentPageIndex].areas);

      ensureParamsExists(area!);
      area!.params!.eventRundownFilter!.attributes = selected.map(selected => selected.item._id);
    });
  }

  function onSelectVenues(selected: BubbleTypeaheadValue<BrandVenueType>[]) {
    setFeedTemplate(draft => {
      const area = findNestedArea(draft.pages[currentPageIndex].areas);

      ensureParamsExists(area!);
      area!.params!.eventRundownFilter!.venues = selected.map(selected => selected.item._id);
    });
  }

  function setEventRundownTitle(nextEventRundownTitle: string) {
    setFeedTemplate(draft => {
      const area = findNestedArea(draft.pages[currentPageIndex].areas);

      ensureParamsExists(area!);
      area!.params!.eventRundownTitle = nextEventRundownTitle;
    });
  }

  function setEventRundownDeckPrefix(nextEventRundownDeckPrefix: string) {
    setFeedTemplate(draft => {
      const area = findNestedArea(draft.pages[currentPageIndex].areas);

      ensureParamsExists(area!);
      area!.params!.eventRundownDeckPrefix = nextEventRundownDeckPrefix;
    });
  }

  function handleEventRundownRemoveTimeDashChange(event: ChangeEvent<HTMLInputElement>) {
    const { checked } = event.target;

    setFeedTemplate(draft => {
      const area = findNestedArea(draft.pages[currentPageIndex].areas);

      ensureParamsExists(area!);
      area!.params!.eventRundownRemoveTimeDash = checked;
    });
  }

  function handleEventRundownShowDescriptionChange(event: ChangeEvent<HTMLInputElement>) {
    const { checked } = event.target;

    setFeedTemplate(draft => {
      const area = findNestedArea(draft.pages[currentPageIndex].areas);

      ensureParamsExists(area!);
      area!.params!.eventRundownShowDescription = checked;
    });
  }

  function handleTimeGroupingAdd(timeGrouping: EventRundownTimeGrouping) {
    setFeedTemplate(draft => {
      const area = findNestedArea(draft.pages[currentPageIndex].areas);

      ensureParamsExists(area!);
      area!.params!.eventRundownTimeGrouping!.push(timeGrouping);
    });
  }

  function handleTimeGroupingDelete(index: number) {
    setFeedTemplate(draft => {
      const area = findNestedArea(draft.pages[currentPageIndex].areas);

      area!.params!.eventRundownTimeGrouping!.splice(index, 1);
    });
  }

  function setTimeGroupingName(nextName: string, index: number) {
    setFeedTemplate(draft => {
      const area = findNestedArea(draft.pages[currentPageIndex].areas);

      area!.params!.eventRundownTimeGrouping![index].name = nextName;
    });
  }

  function setTimeGroupingStartTime(nextStartTime: number, index: number) {
    setFeedTemplate(draft => {
      const area = findNestedArea(draft.pages[currentPageIndex].areas);

      area!.params!.eventRundownTimeGrouping![index].startTime = nextStartTime;
    });
  }

  function ensureParamsExists(draftArea: BareFeedArea | FeedArea) {
    if (typeof draftArea?.params === 'undefined') {
      draftArea!.params = {};
    }

    if (typeof draftArea!.params!.eventRundownFilter === 'undefined') {
      draftArea!.params!.eventRundownFilter = {};
    }

    if (typeof draftArea!.params!.eventRundownTimeGrouping === 'undefined') {
      draftArea!.params!.eventRundownTimeGrouping = undefined;
    }
  }

  function setAreaX(nextX: number) {
    setFeedTemplate(draft => {
      const area = findNestedArea(draft.pages[currentPageIndex].areas);
      area!.rectangle.x = nextX;
    });
  }

  function setAreaY(nextY: number) {
    setFeedTemplate(draft => {
      const area = findNestedArea(draft.pages[currentPageIndex].areas);
      area!.rectangle.y = nextY;
    });
  }

  function setAreaWidth(nextWidth: number) {
    setFeedTemplate(draft => {
      const area = findNestedArea(draft.pages[currentPageIndex].areas);
      area!.rectangle.width = nextWidth;
    });
  }

  function setAreaHeight(nextHeight: number) {
    setFeedTemplate(draft => {
      const area = findNestedArea(draft.pages[currentPageIndex].areas);
      area!.rectangle.height = nextHeight;
    });
  }

  function setIsLocked(nextIsLocked: boolean) {
    setFeedTemplate(draft => {
      const area = findNestedArea(draft.pages[currentPageIndex].areas);
      area!.isLocked = nextIsLocked;
    });
  }

  function addParentArea() {
    setFeedTemplate(draft => {
      const newArea = getNewArea();
      draft.pages[currentPageIndex].areas.push(newArea);
    });
    const nextParentIndex = currentPage.areas.length;
    areaIndicesRef.current = [nextParentIndex];
  }

  function deleteParentArea() {
    setFeedTemplate(draft => {
      const firstIndex = areaIndicesRef.current!.slice().shift()!;
      draft.pages[currentPageIndex].areas.splice(firstIndex, 1);
      areaIndicesRef.current = null;
    });
  }

  function addChildArea() {
    setFeedTemplate(draft => {
      const area = findNestedArea(draft.pages[currentPageIndex].areas);
      const newArea = getNewArea();

      if (!area?.children) {
        area!.children = [];
      }

      area!.children.push(newArea);
    });
  }

  function deleteChildArea() {
    setFeedTemplate(draft => {
      const areaIndex = areaIndicesRef.current!.slice().pop()!;
      const areas = findNestedAreas(draft.pages[currentPageIndex].areas);

      areas?.splice(areaIndex, 1);
    });
  }

  function setCustomAreasCss(nextCustomAreasCss: string) {
    setFeedTemplate(draft => {
      draft.customAreasCss = nextCustomAreasCss;
    });
  }

  function upsertCustomStyleElement(customAreasCss: string = '') {
    const styleId = 'customAreasCss';

    let style = document.getElementById(styleId);

    if (!style) {
      style = document.createElement('style');
      style.setAttribute('id', styleId);
    }

    style.innerHTML = customAreasCss;
    document.head.appendChild(style);
  }

  function isValidFeedTemplate(): boolean {
    if (!feedTemplate.name) {
      return false;
    }

    // TODO: check for all areas name.

    return true;
  }

  function handleEventRundownHideEndTime(event: ChangeEvent<HTMLInputElement>) {
    const { checked } = event.target;

    setFeedTemplate(draft => {
      const area = findNestedArea(draft.pages[currentPageIndex].areas);

      ensureParamsExists(area!);
      area!.params!.eventRundownHideEndTime = checked;
    });
  }

  return (
    <div className="container">
      <div className="header bg-blue-lightest">
        <div className="title">
          <span className="uppercase text-sm font-semibold text-smoke-darkest">
            Feed Template Name:{' '}
          </span>
          <span className="text-navy-darkest font-bold">{feedTemplate.name}</span>
        </div>
        <div className="menu">
          <MenuBar {...props} property={activeProperty} />
        </div>
      </div>

      <div className={`left-sidebar bg-${variant}-darker`}>
        <div className="bg-white flex flex-col items-center">
          <BrandLogo logo={activeProperty.brand.logo} />
          <PropertyBanner {...props} property={activeProperty} />
        </div>

        <div className={`flex flex-col relative my-2 p-4 gap-4 bg-${variant}-darker`}>
          <SideBarSection title="Feed Template">
            <div>
              <BubbleSelect
                block
                className="feed-template-select"
                allowUndefined={false}
                value={selectedFeedTemplate}
                options={feedTemplateOptions}
                labelExtractor={opt => opt.label}
                onValue={selected => setActiveFeedTemplate(selected)}
              />
            </div>
            <div>
              <span className="label mb-2">Name</span>
              <BubbleInput
                block
                type="text"
                variant={variant}
                className="sticky top-0"
                value={feedTemplate.name}
                placeholder="Feed template name"
                onValue={setFeedTemplateName}
              />
            </div>
          </SideBarSection>

          <SideBarSection title="Dimension (pixels)">
            <div className="flex gap-2">
              <div>
                <span className="label">Width</span>
                <BubbleInput
                  block
                  type="number"
                  variant={variant}
                  className="sticky top-0"
                  value={feedTemplate.dimensions.width}
                  placeholder="Width in px"
                  onValue={setFeedTemplateWidth}
                />
              </div>

              <div>
                <span className="label">Height</span>
                <BubbleInput
                  block
                  type="number"
                  variant={variant}
                  className="sticky top-0"
                  value={feedTemplate.dimensions.height}
                  placeholder="Height in px"
                  onValue={setFeedTemplateHeight}
                />
              </div>
            </div>
          </SideBarSection>

          <SideBarSection title="Page">
            <div className="pages-select-buttons">
              <BubbleSelect
                block
                className="page-select"
                allowUndefined={false}
                value={selectedPage}
                options={pageOptions}
                labelExtractor={opt => opt.label}
                onValue={selected => onSelectPage(selected)}
              />
              <BubbleButton
                size="small"
                variant={variant}
                onClick={deletePage}
                icon="minus"
                disabled={feedTemplate.pages.length === 1}
              ></BubbleButton>
              <BubbleButton
                size="small"
                variant={variant}
                onClick={addPage}
                icon="plus"
              ></BubbleButton>
            </div>
          </SideBarSection>

          <SideBarSection title="Fonts">
            <div className="fonts-select">
              <BubbleTypeahead
                block
                variant={variant}
                placeholder="Filter fonts shown..."
                menuClassName="max-h-80 overflow-y-auto"
                labelExtractor={opt => opt.name}
                value={selectedFonts}
                options={fontOptions}
                onAdd={handleOnAddFont}
                onRemove={handleOnRemoveFont}
              />
            </div>

            <BubbleFileM
              className="upload-font-input"
              label="Upload Font"
              hideNoneLabel
              files={[]}
              onUploadFile={onUploadFont}
              uploadRule={UPLOAD_RULES.font}
              hintPosition="inside"
            />
          </SideBarSection>

          <SideBarSection title="Background">
            <BubbleFileM
              className="background-image-input"
              label="Image"
              files={files}
              onUploadFile={onUploadBackgroundImage}
              uploadRule={UPLOAD_RULES.feedBackground}
              hintPosition="inside"
            />
          </SideBarSection>
        </div>
      </div>

      <div className="body bg-blue-lightest">
        <div className="wrapper">
          <div
            className="print-program relative"
            style={{
              backgroundImage: `url(${bgImagePath})`,
              width: feedTemplate.dimensions.width,
              height: feedTemplate.dimensions.height,
              marginLeft: feedTemplate.dimensions.width > 1400 ? '30rem' : 0,
            }}
          >
            {createAreas(currentPage.areas)}
          </div>
        </div>
      </div>

      <div className={`flex flex-col right-sidebar bg-${variant}-darker p-4 gap-4`}>
        <AreasTree
          areas={currentPage.areas}
          currentArea={currentArea}
          onBranchClick={onAreaClick}
          hoveredArea={hoveredArea}
          setHoveredArea={setHoveredArea}
        />

        {canViewJsonEditorButton(user) && (
          <BubbleButton variant="navy" onClick={() => setShowJsonEditor(!showJsonEditor)}>
            Open Json Editor
          </BubbleButton>
        )}

        <SideBarSection title="Areas">
          <div className="flex flex-row">
            <div className="w-1/2">
              <span className="label">Parent Area</span>
              <BubbleButton
                size="small"
                variant={variant}
                onClick={deleteParentArea}
                icon="minus"
                disabled={!currentArea}
              ></BubbleButton>

              <BubbleButton
                size="small"
                variant={variant}
                onClick={addParentArea}
                icon="plus"
                className="ml-2"
              ></BubbleButton>
            </div>

            <div className="w-1/2">
              <span className="label">Child Area</span>
              <BubbleButton
                size="small"
                variant={variant}
                onClick={deleteChildArea}
                icon="minus"
                disabled={!currentArea || !isChildArea()}
              ></BubbleButton>

              <BubbleButton
                size="small"
                variant={variant}
                onClick={addChildArea}
                icon="plus"
                disabled={!currentArea}
                className="ml-2"
              ></BubbleButton>
            </div>
          </div>
        </SideBarSection>

        <SideBarSection>
          <div className="flex gap-2">
            <div>
              <span className="label">Area Name</span>
              <BubbleInput
                block
                disabled={!currentArea}
                type="text"
                variant={variant}
                className="sticky top-0"
                value={currentArea?.name}
                placeholder="Area name"
                onValue={setAreaName}
              />
            </div>
            <div>
              <span className="label">Content Type</span>
              <BubbleSelect
                block
                disabled={!currentArea}
                className="content-type-select"
                allowUndefined={false}
                value={selectedContentType}
                options={getContentTypeOptions()}
                labelExtractor={opt => opt.label}
                onValue={selected => setActiveContentType(selected)}
              />
            </div>
          </div>

          {getContentTypeButton()}
          <div className="flex gap-2">
            <div>
              <span className="label">Class Name</span>
              <BubbleInput
                block
                disabled={!currentArea}
                type="text"
                variant={variant}
                className="sticky top-0"
                value={currentArea?.className}
                placeholder="Class name"
                onValue={setAreaClassName}
              />
            </div>
            <div>
              <span className="label">CSS Bundle</span>
              <BubbleInput
                block
                type="text"
                variant={variant}
                className="sticky top-0"
                value={feedTemplate.cssBundle}
                placeholder="CSS Bundle"
                onValue={setCssBundle}
              />
            </div>
          </div>
        </SideBarSection>

        <SideBarSection title="Position & Size (pixels)">
          <div className="flex flex-row gap-2">
            <div>
              <span className="label mb-2">X-Axis</span>
              <BubbleInput
                block
                disabled={isChildArea()}
                type="number"
                variant={variant}
                className="sticky top-0"
                value={currentArea?.rectangle.x}
                placeholder="X in px"
                onValue={setAreaX}
              />
            </div>

            <div>
              <span className="label mb-2">Y-Axis</span>
              <BubbleInput
                block
                disabled={isChildArea()}
                type="number"
                variant={variant}
                className="sticky top-0"
                value={currentArea?.rectangle.y}
                placeholder="Y in px"
                onValue={setAreaY}
              />
            </div>
          </div>

          <div className="flex flex-row gap-2">
            <div>
              <span className="label mb-2">Width</span>
              <BubbleInput
                block
                disabled={isChildArea()}
                type="number"
                variant={variant}
                className="sticky top-0"
                value={currentArea?.rectangle.width}
                placeholder="Width in px"
                onValue={setAreaWidth}
              />
            </div>
            <div>
              <span className="label mb-2">Height</span>
              <BubbleInput
                block
                disabled={isChildArea()}
                type="number"
                variant={variant}
                className="sticky top-0"
                value={currentArea?.rectangle.height}
                placeholder="Height in px"
                onValue={setAreaHeight}
              />
            </div>
          </div>
        </SideBarSection>

        <SideBarSection>
          <div className="flex is-locked">
            <BubbleInput
              type="checkbox"
              disabled={!currentArea}
              checked={currentArea?.isLocked! || false}
              onValue={setIsLocked}
              className="mr-2"
              inputClassName="bg-white"
              id="locked-checkbox"
            />
            <label htmlFor="locked-checkbox" className="text-white">
              Locked
            </label>
          </div>
        </SideBarSection>

        <span className="label">Custom CSS</span>
        <BubbleTextArea
          block
          className="custom-areas-css-textarea"
          value={feedTemplate.customAreasCss}
          onValue={setCustomAreasCss}
          rows={4}
        />

        <div className="flex cascade-update">
          <BubbleInput
            type="checkbox"
            disabled={!isValidFeedTemplate()}
            checked={shouldCascadeUpdate}
            onValue={setShouldCascadeUpdate}
            className="mr-2"
            inputClassName="bg-white"
            id="cascade-update-checkbox"
          />
          <label htmlFor="cascade-update-checkbox" className="text-white">
            Cascade Update
          </label>
        </div>
        <div className="text-center">
          <BubbleButton
            variant="lime"
            block
            disabled={!isValidFeedTemplate()}
            onClick={onSave}
            icon="floppy-disk"
          >
            Save
          </BubbleButton>
        </div>
      </div>

      {showContentTypePopup && (
        <Popup
          title={`Content Type Params - ${currentArea?.contentType}`}
          onClose={() => setShowContentTypePopup(false)}
        >
          <ContentTypeParams
            contentType={currentArea?.contentType!}
            segments={currentArea?.params?.eventRundownFilter?.segments}
            levels={currentArea?.params?.eventRundownFilter?.levels}
            attributes={currentArea?.params?.eventRundownFilter?.attributes}
            venues={currentArea?.params?.eventRundownFilter?.venues}
            eventRundownTitle={currentArea?.params?.eventRundownTitle}
            eventRundownDeckPrefix={currentArea?.params?.eventRundownDeckPrefix}
            eventRundownRemoveTimeDash={currentArea?.params?.eventRundownRemoveTimeDash}
            eventRundownShowDescription={currentArea?.params?.eventRundownShowDescription}
            eventRundownTimeGrouping={currentArea?.params?.eventRundownTimeGrouping}
            onSelectSegments={onSelectSegments}
            onSelectLevels={onSelectLevels}
            onSelectAttributes={onSelectAttributes}
            onSelectVenues={onSelectVenues}
            setEventRundownTitle={setEventRundownTitle}
            setEventRundownDeckPrefix={setEventRundownDeckPrefix}
            handleEventRundownRemoveTimeDashChange={handleEventRundownRemoveTimeDashChange}
            handleEventRundownShowDescriptionChange={handleEventRundownShowDescriptionChange}
            handleTimeGroupingAdd={handleTimeGroupingAdd}
            handleTimeGroupingDelete={handleTimeGroupingDelete}
            setTimeGroupingName={setTimeGroupingName}
            setTimeGroupingStartTime={setTimeGroupingStartTime}
            eventRundownHideEndTime={currentArea?.params?.eventRundownHideEndTime}
            handelEventRundownHideEndTime={handleEventRundownHideEndTime}
          />
        </Popup>
      )}

      {showJsonEditor && (
        <Popup hideHeader onClose={() => setShowJsonEditor(false)} position="full">
          <div style={{ width: '1000px', background: 'rgb(246, 246, 246)' }}>
            <FeedBlueprintJson data={feedTemplate} onUpdate={setFeedTemplate} />
          </div>
        </Popup>
      )}
    </div>
  );
};

export default FeedBlueprintEditor;

const getNewFeedTemplate = (property: Property): BareFeedTemplate => {
  const width = 800;
  const height = 1200;

  return {
    _id: '',
    name: '',
    property: property._id,
    dimensions: {
      width,
      height,
    },
    renderedDimensions: {
      width,
      height,
    },
    dpi: 100,
    pages: [getNewPage()],
    fonts: [],
    cssBundle: 'basic',
  };
};

const getNewPage = (): BareFeedPage => ({
  _id: new ObjectId().toString(),
  image: '',
  areas: [],
  rundownFontSize: 0,
});

const getNewArea = (): BareFeedArea & FeedArea => ({
  _id: new ObjectId().toString(),
  name: 'undefined',
  rectangle: {
    x: 0,
    y: 0,
    width: 100,
    height: 100,
  },
  style: {},
  className: '',
  contentType: 'static',
  params: {},
  isLocked: false,
});

const getContentTypeOptions = (): ContentTypeOptions[] => {
  const contentTypes: ContentType[] = [
    'filtered-rundown',
    'static',
    'location-image',
    'date',
    'event',
    'venue',
    'venue-rundown',
    'venue-meal-periods',
    'column',
    'row',
    'wellness-rundown',
    'text',
    'image',
    'date-widget',
    'multi-day',
  ];

  return contentTypes.map(contentType => ({
    value: contentType,
    label: contentType,
  }));
};

type SideBarSectionProps = {
  title?: string;
};

const SideBarSection: React.FC<SideBarSectionProps> = ({ title, children }) => {
  return (
    <div className="grid gap-y-4 mb-4">
      {title && <span className="label font-bold">{title}</span>}
      {children}
    </div>
  );
};
